import { makeStyles } from "@mui/styles";
import { useDatePickerStyles } from "components/disw/DatePickerStyle";
import FormikInputLabel from "components/disw/FormikInputLabel";
import DateBox from "devextreme-react/date-box";
import { getMonthStartDate } from "../../features/billing-and-metering/service-products/helper/periodHelpers";
import { useInputLabelStyles } from "./InputLabelStyle";

const useStyles = makeStyles(theme => ({
  container: {
    display: "flex",
    flexDirection: "row",
    gap: 16
  },
  field: {
    minWidth: 0,
    flex: 1
  }
}));

interface MonthRangePickerProps {
  period: { startMonth: Date | undefined; endMonth: Date | undefined };
  onRangeChanged: (period: {
    startMonth: Date | undefined;
    endMonth: Date | undefined;
  }) => void;
  isErrorInStartMonthPicker?: boolean;
  isErrorInEndMonthPicker?: boolean;
  startMonthErrorText?: string;
  endMonthErrorText?: string;
  required?: boolean;
}

export const MonthRangePicker = ({
  period,
  onRangeChanged,
  isErrorInStartMonthPicker,
  isErrorInEndMonthPicker,
  startMonthErrorText,
  endMonthErrorText,
  required = false
}: MonthRangePickerProps) => {
  const classes = useStyles();
  const labelClasses = useInputLabelStyles();
  const datePickerClasses = useDatePickerStyles();
  const normalizedStartMonth =
    period.startMonth && getMonthStartDate(period.startMonth);
  const normalizedEndMonth =
    period.endMonth && getMonthStartDate(period.endMonth);
  return (
    <div className={classes.container}>
      <div className={classes.field}>
        <FormikInputLabel
          name="startMonth"
          required={required}
          label="Start Month"
          isError={isErrorInStartMonthPicker}
          errorMessage={startMonthErrorText}
          labelClasses={labelClasses}
        />
        <DateBox
          id="start-month-picker"
          type="date"
          value={normalizedStartMonth}
          max={normalizedEndMonth}
          onValueChanged={e => {
            const newStart = e.value && getMonthStartDate(e.value);
            if (
              !normalizedEndMonth ||
              !newStart ||
              newStart <= normalizedEndMonth
            ) {
              onRangeChanged({
                startMonth: newStart,
                endMonth: normalizedEndMonth
              });
            }
          }}
          displayFormat="MMMM yyyy"
          pickerType="calendar"
          calendarOptions={{
            maxZoomLevel: "year",
            minZoomLevel: "decade"
          }}
          useMaskBehavior={true}
          openOnFieldClick={true}
          inputAttr={{
            "aria-label": "Start Month",
            placeholder: "Select start month",
            readOnly: true
          }}
          className={
            isErrorInStartMonthPicker
              ? datePickerClasses.dateBoxWithError
              : datePickerClasses.dateBox
          }
          dropDownOptions={{
            wrapperAttr: {
              class: datePickerClasses.dateBoxDropDown
            }
          }}
        />
      </div>

      <div className={classes.field}>
        <FormikInputLabel
          name="endMonth"
          required={required}
          label="End Month"
          isError={isErrorInEndMonthPicker}
          errorMessage={endMonthErrorText}
          labelClasses={labelClasses}
        />
        <DateBox
          id="end-month-picker"
          type="date"
          value={normalizedEndMonth}
          min={normalizedStartMonth}
          onValueChanged={e => {
            const newEnd = e.value && getMonthStartDate(e.value);
            if (
              !normalizedStartMonth ||
              !newEnd ||
              newEnd >= normalizedStartMonth
            ) {
              onRangeChanged({
                startMonth: normalizedStartMonth,
                endMonth: newEnd
              });
            }
          }}
          displayFormat="MMMM yyyy"
          pickerType="calendar"
          calendarOptions={{
            maxZoomLevel: "year",
            minZoomLevel: "decade"
          }}
          useMaskBehavior={true}
          openOnFieldClick={true}
          inputAttr={{
            "aria-label": "End Month",
            placeholder: "Select end month",
            readOnly: true
          }}
          className={
            isErrorInEndMonthPicker
              ? datePickerClasses.dateBoxWithError
              : datePickerClasses.dateBox
          }
          dropDownOptions={{
            wrapperAttr: {
              class: datePickerClasses.dateBoxDropDown
            }
          }}
        />
      </div>
    </div>
  );
};
